import moment from 'moment';
import { NewPatient } from '../store/actions/visit';
import { getAgeInMonths } from '../store/utils';
import { INIT_VISIT } from './constants';
import fetchWithRefresh from './fetchWithRefresh';
import { updateAppointment } from '../firestore/appointments';
import { Timestamp } from 'firebase/firestore';
import { getDefiniteMActiveId, getQueueConfig, isOnNewQueueView } from '../store/localstorage';
import { getGById, getMe } from '../store/idbHandler';
import { Appointment } from './fetchAppointmentsV2';
import { DB_TABLES } from '../store/dbTables';
import { track } from '../tracking';
import { Actor, AptActivityType } from '../tracking/mixpanel';

type Params = {
    aid: string;
    clinicId: string;
    startedAt: string;
    docId: string;
    patient?: NewPatient;
    pid?: string;
    abortVisitId?: string;
};

export interface Payload {
    aid: string;
    clinic: string;
    patient: Patient;
    started_at: string;
    abortVisitId?: string;
}
export interface Patient {
    id?: string;
    profile?: Profile;
}
export interface Profile {
    personal: Personal;
}
export interface Personal {
    name: string;
    phone?: Phone;
    age?: Age;
    gender: string;
}
export interface Phone {
    c?: string;
    n?: string;
}
export interface Age {
    dob?: string;
    ageInMonths?: number;
}

export default async ({
    aid,
    clinicId,
    startedAt,
    docId,
    patient,
    pid,
    abortVisitId,
}: Params): Promise<void> => {
    if (isOnNewQueueView()) {
        if (!aid) {
            return Promise.reject('Invalid appointment id');
        }

        const apt = await getGById<Appointment | undefined>(
            getDefiniteMActiveId(),
            DB_TABLES.appointments,
            aid,
        );

        if (!apt) {
            if (!window.bookSlot) {
                return Promise.reject('Appointment bookSlot not found');
            }

            await window.bookSlot({
                aid,
                channel: 'Walkin',
                mode: 'in-clinic',
                clinicId: clinicId,
                doctorId: docId,
                patient: {
                    oid: (pid || patient?.localId || patient?.id) as string,
                    allowMultiBooking: getQueueConfig()?.apt_config?.allow_pt_multi_apt,
                },
                startTime: new Date().toISOString(),
                endTime: new Date().toISOString(),
                action: 'CK',
            });
        }

        const visit_start = new Date();
        await updateAppointment({
            aid,
            data: {
                visit_start: Timestamp.fromDate(new Date(visit_start)),
                status: 'OG',
                date: Timestamp.fromDate(moment(visit_start).startOf('day').toDate()),
                // token,
                visit_checkin: Timestamp.fromDate(new Date(apt?.visit_checkin || new Date())),
                full_date: Timestamp.fromDate(new Date(visit_start)),
            },
        });

        const me = getMe();

        track(AptActivityType.VISIT_STARTED, {
            aid,
            actor: me?.user['is-d-s'] ? Actor.STAFF : Actor.DOCTOR,
            actor_oid: me?.user.oid,
            attribute: 'dr-web',
            actor_name: me?.user.fn,
        });

        return Promise.resolve();
    }

    const ageInMonths = patient && patient.age && getAgeInMonths(patient.age);

    const bePatient: Patient = {
        id: pid,
        profile: patient && {
            personal: {
                name: patient.name,
                phone: patient.phoneNumber
                    ? {
                          c: '+91',
                          n: patient.phoneNumber,
                      }
                    : undefined,
                age: patient.age?.dob
                    ? { dob: moment(patient.age?.dob).format('YYYY-MM-DD') }
                    : ageInMonths
                    ? { ageInMonths: ageInMonths }
                    : undefined,
                gender: patient.gender,
            },
        },
    };

    const playload: Payload = {
        aid: aid,
        clinic: clinicId,
        patient: bePatient,
        started_at: startedAt,
        abortVisitId,
    };

    return fetchWithRefresh(INIT_VISIT(docId), {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(playload),
    }).then((res) => res.json());
};
